import { BidderSeat, CreateBidderSeat, displayBidderSeatName, productOfBidderSeatType, SPARK_DEMAND_DEFAULT_BID_ADJUSTMENT, toSparkBidderCode } from '@pubstack/common/src/bidder-seat'
import { isMarketplaceAlias } from '@pubstack/common/src/bidderCatalog'
import { isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { byName, compareNames } from '@pubstack/common/src/sort'
import { Stack } from '@pubstack/common/src/stack'
import { UPGRADE_OFFER_FEATURE } from '@pubstack/common/src/upgrade'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { Alert } from '@pubstack/ui/components/Alert'
import { useAtomValue } from 'jotai'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePbstckBidderGlobalConfig } from '~/api/admin/pbstck-bidder-global-config.api'
import { post } from '~/api/api-client'
import { useSites } from '~/api/api.hook'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useCatalog } from '~/api/catalog.api'
import { useScopeLevelStack } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { BidderIdentity } from '~/components/catalog/BidderIdentity'
import { NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { PageContent } from '~/components/PageContent'
import Table, { handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import { openJsonInNewTab } from '~/utils/fileDownload'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { TabProp, Tabs } from '~/components/Tabs'
import { Tag } from '~/components/Tag'
import { useToast } from '~/components/Toast/useToasts'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Tooltip } from '~/components/Tooltip'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScope } from '~/hooks/useScope'
import { useIsSparkOnboarded } from '~/hooks/useSparkBidder'
import { useIsSparkForPubOnboarded, useSparkForPubStaffAccess } from '~/hooks/useSparkForPub'
import { PATH_BY_PRODUCT } from '~/hooks/useProduct'
import { configurationState } from '~/state'
import { CreateSeatFlyout } from '../common/CreateSeatFlyout'
import { FilterBar } from '../common/FilterBar'
import { SeatQuickEditFlyout } from './bidder-seat/SeatQuickEditFlyout'
import {
  activatedSparkDemandBidderCodes,
  compareSeatMapping,
  countMappedSites,
  emptySeatTabInvitation,
  isInternalSeatTab,
  isScopeLevelSeat,
  isSeatMapped,
  MediaCounts,
  mediaCountOfSeat,
  missingSparkDemandBidderCodes,
  SEAT_TAB_LABEL,
  SEAT_TABS,
  SeatTab,
  seatTabOfLabel,
  tabOfSeat,
  unreachableSeatsWarning,
  visibleSeatTabs,
} from './myBidderSeatTabs'
import { SeatTypeChip } from './SeatTypeChip'
import { seatTypeLabel } from './seatType'

/** The width the rule lists give a search that owns its row — same value, so the two pages match. */
const SEARCH_CLASSES = 'w-2/5 max-w-[592px] shrink-0'

// Constants
const ILLUSTRATION_ICON_SIZE_PX = 190

// Tracking labels. The page is product-neutral now, so `common` is the prefix — `adm-`/`pbs-` would
// claim a product the URL no longer carries.
const GO_TO_MAPPING_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-mapping-navigate'
const QUICK_EDIT_SEAT_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-seat-quick-edit-view'
const QUICK_EDIT_SEAT_CLOSE_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-seat-quick-edit-close'
const GO_TO_CATALOG_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-catalog-navigate'
const RESET_FILTERS_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-filters-restore'
const BACK_TO_SITE_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-back-to-site-navigate'
const NEW_SEAT_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-new'
const SEAT_TAB_TRACKING_LABEL: Record<SeatTab, TrackingLabel> = {
  'client-side': 'common-my-bidder-seats-tab-client-side-apply',
  'server-side': 'common-my-bidder-seats-tab-server-side-apply',
  'spark-for-pub': 'common-my-bidder-seats-tab-spark-for-pub-apply',
  'spark-demand': 'common-my-bidder-seats-tab-spark-demand-apply',
}
const ADD_SPARK_DEMAND_SEAT_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-spark-demand-new'
const CREATE_ALL_SPARK_DEMAND_SEATS_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-spark-demand-create-all-new'
const CREATE_ALL_SPARK_DEMAND_SEATS_CONFIRM_TRACKING_LABEL: TrackingLabel = 'common-my-bidder-seats-spark-demand-create-all-confirm'

// No Status filter. It read "All / Mapped / Not mapped", which is the Mapping column said twice — and
// that column sorts, so the one question the filter answered was already a click away on a list this
// short. Its `Spark For Pub` entry had already become a tab, for the same reason in reverse: a filter and
// a tab over the same set of seats is one control too many.

const formatBidAdjustment = (seat: BidderSeat): string | undefined =>
  seat.bidAdjustment?.enabled && seat.bidAdjustment.value !== undefined ? `${(seat.bidAdjustment.value * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%` : undefined

/**
 * Seat name first, bidder as tiebreak — both case-insensitively, since seat names are free text.
 *
 * The name the user gave the seat is what the row now leads with, so it is what the list is ordered by. Two
 * seats of the same bidder are told apart by that name and by nothing else, which is the reason it is the
 * one thing on the row rather than the second column.
 */
const bySeatNameThenBidder = (a: SeatRow, b: SeatRow): number => byName(a, b) || compareNames(a.displayName, b.displayName)

type SeatRow = {
  seat: BidderSeat
  /** `bidderCode` lowercased, so the default sort groups `appnexus` with `Appnexus`. */
  bidderCode: string
  /**
   * The company as the catalog names it. It is the tooltip on the logo rather than a column of its own —
   * the adapter code the seat actually stores would file Xandr under `a` and Magnite under `r`, so it is
   * never the thing shown, only the thing quoted in a support thread.
   */
  displayName: string
  name: string
  /** Pubstack's own demand: only ever on a staff member's screen. */
  isInternal: boolean
  typeLabel: string
  /** `-1` when disabled, so the column sorts adjusted seats away from unadjusted ones. */
  bidAdjustment: number
  mappedSitesCount: number
  /** Sentinel-aware, so a scope-level Spark Demand seat does not read as unmapped. */
  isMapped: boolean
  /** Only supports in-app media once integrated server-side — worth a warning next to the adapter. */
  isInAppOnly: boolean
  /** A Spark Demand seat on an adapter the Spark global config leaves off: carried by no stack, so it bids on nothing. */
  isGloballyDisabled: boolean
  /** A client-side seat Pubstack sells through the marketplace — recognised by its `-pbstck` name. */
  isMarketplace: boolean
}

export const MyBidderSeatsPage: FunctionComponent = () => {
  // Context
  const [searchParams] = useSearchParams()
  const backToSite = searchParams.get('backToSite')
  const user = useAuthenticatedUser()
  const scopeId = user.currentScopeId
  const scope = useScope()
  const isPubstackUser = isPubstackUserRole(user.userRoles)
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)

  // State
  const [search, setSearch] = useState<string>('')
  const [mediaCounts, setMediaCounts] = useState<MediaCounts>({ adManaged: 0, pbsStandalone: 0, crossInventory: 0 })
  const [isBulkCreating, setIsBulkCreating] = useState(false)
  // In the URL rather than in component state, and pushed rather than replaced: a seat opens a flyout and
  // a mapping page navigates away, so Back is the way most people come back here — and it has to land on
  // the tab they left, not on the first one. Same idiom as the deployment center.
  //
  // No default. Which tabs exist depends on hooks that have not run yet, so the first render picks no tab
  // at all and `activeTab` below resolves one; a default here would name a tab this scope may not have.
  // `parseAsStringLiteral` also drops a hand-typed `?tab=` that is not a tab at all.
  //
  // `shallow: false`, so nuqs also replays this write through React Router's own `navigate` rather than
  // only through a raw `history.pushState`. `FilterBar` (and this page's own `searchParams` above) read
  // the URL via React Router's `useSearchParams`, which never observes a shallow write — so switching tabs
  // then typing a search term clobbered `tab` back to nothing the moment the debounced search write fired,
  // React Router's stale snapshot not having `tab` to carry forward.
  const [selectedTab, setSelectedTab] = useQueryState<SeatTab>('tab', parseAsStringLiteral(SEAT_TABS).withOptions({ history: 'push', shallow: false }))
  const [columns, setColumns] = useState<TableColumns<SeatRow>>([
    // The seat leads, since its name is the one thing that tells two rows of the same bidder apart, and the
    // adapter follows in a column of its own — sortable, so the list can still be read bidder by bidder.
    { name: 'Name', isSortable: true, order: 'ascending', attributeSort: 'name', sort: bySeatNameThenBidder },
    { name: 'Adapter', isSortable: true, order: 'none', attributeSort: 'displayName' },
    { name: 'Type', isSortable: true, order: 'none', attributeSort: 'typeLabel' },
    { name: 'Bid adjustment', isSortable: true, order: 'none', attributeSort: 'bidAdjustment' },
    // The column renders mapping status ("Not mapped", "Mapped on 1/4 media"), which "Stats" does not describe.
    { name: 'Mapping', isSortable: true, order: 'none', attributeSort: 'mappedSitesCount', sort: (a, b) => compareSeatMapping(a.seat, b.seat) },
  ])

  // API Hooks
  // Both products' seats, on one list. `adm` is the catalog superset — the pbs one carries only
  // server-side compatible bidders, and every seat here needs its adapter looked up.
  const { data: admBidderSeats, isLoading: isLoadingAdmSeats, refetch: refetchAdmSeats } = useBidderSeats('adm')
  const { data: pbsBidderSeats, isLoading: isLoadingPbsSeats, refetch: refetchPbsSeats } = useBidderSeats('pbs')
  const { data: modules, isLoading: isLoadingCatalog } = useCatalog('adm')
  const { all } = useSites(null)

  // Asked of the scope, not of the viewer: a Pubstack user has every feature synthesised for whichever
  // account they are in, and would otherwise be shown four tabs on a scope that has bought one thing.
  // The **access** features on purpose, matching the catalog — a tab is a place to create a seat, and
  // creating one is a write.
  const hasAdManagement = scope.hasFeature(UPGRADE_OFFER_FEATURE.one)
  const { hasNode } = useNodeStaffAccess()
  const isSparkOnboarded = useIsSparkOnboarded()
  const isSparkForPubOnboarded = useIsSparkForPubOnboarded()
  const { canCreateSeat: canCreateSparkForPubSeat } = useSparkForPubStaffAccess()

  // Whether the internal listing is worth asking for. It cannot wait on `visibleSeatTabs` — that needs the
  // rows this query fetches.
  //
  // Either provisioning, not just Spark Demand's: one listing feeds both gated tabs, and since the split
  // the Spark for Pubs tab's entire content is the internal half of each pair. A scope onboarded on Spark
  // for Pubs alone would otherwise show staff that tab and never fetch a thing to put on it.
  const canReadInternalSeats = isPubstackUser && (isSparkOnboarded || isSparkForPubOnboarded)
  // Pubstack's own seats, which the two customer-facing listings deliberately exclude (the API defaults
  // `isInternal` to false rather than leaving it unset, so internal seats never leak into a customer's).
  // Without this third call both gated tabs have nothing to list whatever the scope carries.
  const { data: internalBidderSeats, isLoading: isLoadingInternalSeats, refetch: refetchInternalSeats } = useBidderSeats('pbs', { isInternal: true }, canReadInternalSeats)

  // Spark Demand's own provisioning, which is narrower: these two answer what "Create all Spark Demand
  // seats" may create, and mean nothing on a scope that runs Spark for Pubs alone.
  const canReadSparkDemandSetup = isPubstackUser && isSparkOnboarded

  // Both are admin URLs and 403 for a customer, so neither is allowed to fire on a customer's page.
  // The global config is what says which adapters Spark actually runs, and therefore which seats "create
  // all" is allowed to create — the button below cannot answer anything honest until it has arrived.
  const { data: pbstckBidderGlobalConfig, isLoading: isLoadingGlobalConfig } = usePbstckBidderGlobalConfig(canReadSparkDemandSetup)
  const enabledSparkBidderCodes = pbstckBidderGlobalConfig?.enabledBidderCodes ?? []

  // Every adapter Spark can carry demand for. "Create all" narrows this by the global config below —
  // compatibility says a seat *could* exist, activation says a stack would actually carry it.
  const { data: sparkDemandCatalog, isLoading: isLoadingSparkDemandCatalog } = useCatalog('pbs', { isPbstckBidderCompatible: true }, canReadSparkDemandSetup)

  // No type is filtered out here any more. Which seats a customer may see is the API's answer to give,
  // and it already gives it: the two listings above are `isInternal: false`, so Pubstack's own demand
  // never reaches a customer, while the customer's *own* Spark for Pubs seat does — as it should, since
  // they created it from the catalog. Filtering `spark_for_pubs` out again here would hide it from them.
  const bidderSeats = useMemo(() => [...admBidderSeats, ...pbsBidderSeats, ...internalBidderSeats], [admBidderSeats, pbsBidderSeats, internalBidderSeats])
  const isLoadingBidderSeats = isLoadingAdmSeats || isLoadingPbsSeats || isLoadingInternalSeats
  const refetchBidderSeats = () => {
    refetchAdmSeats()
    refetchPbsSeats()
    if (canReadInternalSeats) refetchInternalSeats()
  }

  // Hooks
  const breadcrumbs = useBreadcrumbs()
  const { canCreate, disabledReason } = useScopePermissions()
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const toast = useToast()

  const isLoading = isLoadingBidderSeats || isLoadingCatalog

  const rows: SeatRow[] = bidderSeats.map((seat) => ({
    seat,
    bidderCode: seat.bidderCode.toLowerCase(),
    // Falls back to the code: a seat can outlive its catalog entry, and an empty cell would read as a
    // seat with no bidder at all.
    displayName: modules.find((module) => module.id === seat.bidderCode)?.displayName ?? seat.bidderCode,
    // The Spark twin's `-int` comes off here, so the row reads as the seat rather than as a second
    // bidder — and so the search below matches what is actually on screen.
    name: displayBidderSeatName(seat),
    // Asked of the seat's own flag rather than of its type. Since the split, `spark_for_pubs` answers
    // both ways: the publisher's half is theirs and wears the customer tag on Server-side, the Spark
    // team's half is internal and wears Lava on the tab that only staff reaches.
    isInternal: seat.isInternal ?? false,
    typeLabel: seatTypeLabel(seat),
    bidAdjustment: seat.bidAdjustment?.enabled ? (seat.bidAdjustment.value ?? -1) : -1,
    mappedSitesCount: countMappedSites(seat),
    isMapped: isSeatMapped(seat),
    isInAppOnly: !!modules.find((m) => m.id === seat.bidderCode)?.supportedEnvs?.every((env) => env === 'inapp'),
    // The global config decides which Spark Demand seats reach a stack; the seat existing says nothing.
    isGloballyDisabled: seat.type === 'spark_demand' && !!pbstckBidderGlobalConfig && !pbstckBidderGlobalConfig.enabledBidderCodes.includes(seat.bidderCode),
    isMarketplace: isMarketplaceAlias(seat.name),
  }))

  // Filter by name and search
  const filteredRows = rows.filter((row) => row.name.toLowerCase().includes(search.toLowerCase()))

  /** Each tab owns a slice of the same list, sorted the same way — the tabs are a filter, not four pages. */
  const rowsOfTab = (tab: SeatTab) =>
    // `handleTableSort` breaks ties by input order, so pre-sorting makes every column fall back to name-then-bidder.
    handleTableSort(columns, [...filteredRows.filter((row) => tabOfSeat(row.seat) === tab)].sort(bySeatNameThenBidder))

  const clientSideRows = rowsOfTab('client-side')
  const serverSideRows = rowsOfTab('server-side')
  const sparkForPubRows = rowsOfTab('spark-for-pub')
  const sparkDemandRows = rowsOfTab('spark-demand')

  // From `rows`, not from the filtered ones: a search that matches nothing must empty the table, never
  // remove the tab under the user mid-keystroke.
  const tabsWithSeats = [...new Set(rows.map((row) => tabOfSeat(row.seat)))]
  const tabs = visibleSeatTabs({ isPubstackUser, hasAdManagement, hasNode, canCreateSparkForPubSeat, isSparkOnboarded, tabsWithSeats })
  const showClientSideTab = tabs.includes('client-side')
  const showServerSideTab = tabs.includes('server-side')
  const showSparkForPubTab = tabs.includes('spark-for-pub')
  const showSparkDemandTab = tabs.includes('spark-demand')
  // `visibleSeatTabs` never returns empty, so `tabs[0]` always resolves. A tab can also disappear under a
  // selection that was valid when it was made — the flags it hangs off arrive from hooks and from the seat
  // list — so falling back to the first tab beats rendering a body for a tab no longer in the bar.
  const activeTab: SeatTab = selectedTab && tabs.includes(selectedTab) ? selectedTab : tabs[0]

  const handleNewSeatClick = () => {
    flyout.open(
      CreateSeatFlyout,
      { color: Colors.Kaiminus, closeTrackingLabel: `${NEW_SEAT_TRACKING_LABEL}-close` },
      {
        scopeId,
        type: 'pbs',
        // Whichever product the chosen type lands the seat in, it appears on this list — which is the
        // point of merging them, and why the toast no longer has to send anyone anywhere else.
        onSeatCreated: (name: string) => {
          refetchBidderSeats()

          toast.success(
            <>
              <strong>{name}</strong> successfully added to your bidder seats.
            </>
          )
        },
        onClose: () => flyout.close(),
      }
    )
  }

  const handleAddSparkDemandSeatClick = () => {
    flyout.open(
      CreateSeatFlyout,
      { color: Colors.Kaiminus, closeTrackingLabel: `${ADD_SPARK_DEMAND_SEAT_TRACKING_LABEL}-close` },
      {
        scopeId,
        type: 'spark-demand',
        onSeatCreated: (name: string) => {
          refetchBidderSeats()
          toast.success(
            <>
              <strong>{name}</strong> successfully added to the Spark Demand seats.
            </>
          )
        },
        onClose: () => flyout.close(),
      }
    )
  }

  // Activated, not merely compatible: an adapter the Spark settings leave off is carried by no stack, and
  // a seat created for it would only ever be a row that bids on nothing.
  const activatedSparkDemandBidders = activatedSparkDemandBidderCodes(sparkDemandCatalog, enabledSparkBidderCodes)
  const missingSparkDemandBidders = missingSparkDemandBidderCodes(sparkDemandCatalog, bidderSeats, enabledSparkBidderCodes)
  // An empty candidate set has four causes and they must not share a label: never asked for, still
  // loading, the Spark settings switch nothing on, or every activated adapter already has its seat. Only
  // the last two are answers — the first two are the absence of one, and say so by saying nothing.
  //
  // "Never asked for" is a real state, not a defensive branch: both queries hang off `canReadInternalSeats`,
  // while the tab itself opens for a Pubstack user whose scope merely *holds* Spark Demand seats. Such a
  // user reaches this button with neither query fired, and a disabled query reports no loading, so without
  // this the button would claim no adapter is activated on the strength of a question never put.
  //
  // The internal listing is part of the answer too: `missingSparkDemandBidderCodes` subtracts the seats
  // that already exist, and those arrive only with it — until then a fully-provisioned scope reads as
  // all-missing, and the button would offer to recreate every seat.
  const isCreateAllCandidateSetUnknown = !canReadSparkDemandSetup || isLoadingGlobalConfig || isLoadingSparkDemandCatalog || isLoadingInternalSeats
  const createAllSeatsLabel = isCreateAllCandidateSetUnknown
    ? 'Create all seats'
    : activatedSparkDemandBidders.length === 0
      ? 'No activated adapter'
      : missingSparkDemandBidders.length === 0
        ? 'All seats created'
        : `Create all seats (${missingSparkDemandBidders.length})`

  const createAllSparkDemandSeats = async () => {
    setIsBulkCreating(true)
    // Sequential on purpose: seat creation validates the name against every existing seat in the scope,
    // and a burst of parallel POSTs is a lot of load for a button that runs once per scope.
    //
    // The raw client rather than `useCreateBidderSeat`: the hook invalidates the seat listings after
    // every call, refetching both mounted pbs listings per created seat mid-loop — the one
    // `refetchBidderSeats` below covers the lot.
    const signal = new AbortController().signal
    const failed: string[] = []
    for (const bidderCode of missingSparkDemandBidders) {
      try {
        await post(
          '/pbs/bidder-seats',
          {
            name: toSparkBidderCode(bidderCode),
            bidderCode,
            type: 'spark_demand',
            isInternal: true,
            platform: 'server',
            mappingDimensions: ['placementId'],
            bidAdjustment: { enabled: true, value: SPARK_DEMAND_DEFAULT_BID_ADJUSTMENT },
          } satisfies CreateBidderSeat,
          signal
        )
      } catch {
        // One adapter failing must not cost the other forty: collect and report at the end.
        failed.push(bidderCode)
      }
    }
    setIsBulkCreating(false)
    refetchBidderSeats()

    const created = missingSparkDemandBidders.length - failed.length
    if (failed.length) {
      toast.alert(`${created} seat${created > 1 ? 's' : ''} created, ${failed.length} failed: ${failed.join(', ')}.`, { icon: 'warning' })
    } else {
      toast.success(`${created} Spark Demand seat${created > 1 ? 's' : ''} created.`)
    }
  }

  const handleCreateAllSparkDemandSeatsClick = () => {
    // Counted against the activated adapters, not the whole compatible catalog: everything outside that
    // set is not a seat being skipped, it is a seat this button was never going to create.
    const skipped = activatedSparkDemandBidders.length - missingSparkDemandBidders.length
    modal.open(ConfirmationModal, {
      title: 'Create all Spark Demand seats',
      message: `${missingSparkDemandBidders.length} seat${missingSparkDemandBidders.length > 1 ? 's' : ''} will be created, one per adapter activated in the Spark settings, named <bidder>-spark.${skipped > 0 ? ` ${skipped} already exist${skipped > 1 ? '' : 's'} and will be skipped.` : ''}`,
      trackingLabel: CREATE_ALL_SPARK_DEMAND_SEATS_CONFIRM_TRACKING_LABEL,
      onCancel: () => modal.close(),
      onConfirm: async () => {
        modal.close()
        await createAllSparkDemandSeats()
      },
    })
  }

  // Spark Demand and Spark for Pubs open in a flyout over the list instead — both are staff-only,
  // scope-level seats with nothing on `BidderSeatPage` worth a screen of its own beyond bid adjustment and
  // the mapping table. Every other tab still navigates: it carries per-media filters and CSV import/export
  // a flyout has no room for. By the seat's own product, not the page's — the seat page is still
  // per-product, and reads its data from that product's API. Same rule the media list uses to route a row.
  const handleRowClick = (seat: BidderSeat) => {
    if (isInternalSeatTab(tabOfSeat(seat))) {
      flyout.open(SeatQuickEditFlyout, { color: Colors.Kaiminus, width: '816px', closeTrackingLabel: QUICK_EDIT_SEAT_CLOSE_TRACKING_LABEL }, { bidderSeat: seat })
      return
    }
    navigate(
      {
        pathname: `${PATH_BY_PRODUCT[productOfBidderSeatType(seat.type)]}/integrations/my-bidder-seats/${seat.id}`,
        search: searchParams.toString(),
      },
      { state: { module: seat } }
    )
  }

  // Two calls rather than one with both flags, because the two answers mean different things — see
  // `MediaCounts`. The union rather than the sum: a site can be ad-managed *and* Node standalone, and
  // adding the two listings would put more media in the denominator than the scope actually has.
  const fetchSites = async () => {
    const [admSites, pbsSites] = await Promise.all([all.get({ getAdManagedSites: true }), all.get({ getPbsStandaloneSites: true })])
    setMediaCounts({ adManaged: admSites.length, pbsStandalone: pbsSites.length, crossInventory: new Set([...admSites, ...pbsSites].map((site) => site.id)).size })
  }

  useEffect(() => {
    // Legitimate data load on mount (async fetch then setState).
    // oxlint-disable-next-line react/set-state-in-effect
    fetchSites()
  }, [])

  const goToCatalog = () => navigate({ pathname: '/monetization/inventory/catalog', search: searchParams.toString() })

  /**
   * Nothing to list, and a way out of it. Both empty states below render this — the whole page when the
   * scope has no integration at all, and a single tab when only that one is empty — so the two can never
   * drift apart on the thing that matters, which is where the button goes.
   */
  const noSeatsMessage = (title: string) => (
    <div className={'flex size-full flex-col items-center justify-center'}>
      <IllustrationMessage iconName="night_sky" iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`} title={title} />
      <Button onClick={goToCatalog} variant="primary" trackingLabel={GO_TO_CATALOG_TRACKING_LABEL}>
        Go to catalog
      </Button>
    </div>
  )

  /**
   * The Type column, everywhere except the client-side tab.
   *
   * That tab is defined by its type, so the column repeated the tab's own name down every row. The other
   * three keep it: Server-side holds the deprecated seats too, and the two Spark tabs are read alongside
   * them. Filtered from the shared array rather than declared per tab, so the entries stay the same
   * objects and `onColumnSort` — which matches a clicked heading by identity — still finds them.
   */
  const columnsOfTab = (tab: SeatTab) => (tab === 'client-side' ? columns.filter((column) => column.name !== 'Type') : columns)

  /** One table, rendered per tab over its own slice — the columns and their sort are shared on purpose. */
  const seatsTable = (tab: SeatTab, tabRows: SeatRow[]) => (
    <Table columns={columnsOfTab(tab)} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
      {tabRows.length > 0 ? (
        tabRows.map((row) => {
          const bidAdjustment = formatBidAdjustment(row.seat)
          return (
            <TableRow key={row.seat.id} clickable={{ onClick: () => handleRowClick(row.seat), trackingLabel: isInternalSeatTab(tab) ? QUICK_EDIT_SEAT_TRACKING_LABEL : GO_TO_MAPPING_TRACKING_LABEL }}>
              <TableCell>
                <div className={'flex min-w-0 items-center gap-2'}>
                  {/* The seat's own name on the row, the adapter code in the tooltip: same shape the site
                      mapping view uses, so a seat reads the same wherever it appears. */}
                  <BidderIdentity baseCDNUrl={STATIC_ASSETS_CDN} code={row.seat.bidderCode} name={row.name} bidderName={row.displayName} />
                  {row.isInAppOnly && (
                    <Tooltip className={'flex!'} title="Only supports in-app media when integrated server-side">
                      <Icon width="20px" name="warning" fill={Colors.Warning} />
                    </Tooltip>
                  )}
                  {/* The client-side tab drops the Type column, so the one thing that tells a marketplace
                      seat from a seat the customer set up themselves has nowhere else to go. Grey and
                      `font-normal` for the same reason `SeatTypeChip` is: it is context for the row, not
                      a claim about it. */}
                  {row.isMarketplace && (
                    <Tag className={'shrink-0 items-center font-normal'} color={Colors.Ash}>
                      Marketplace
                    </Tag>
                  )}
                </div>
              </TableCell>
              <TableCell>{row.displayName}</TableCell>
              {tab !== 'client-side' && (
                <TableCell>
                  <div className={'flex items-center gap-1'}>
                    {/* How the seat is integrated, and only that. The product it belongs to followed from the
                      type anyway — Spark Demand is always Node, client-side always One — so a second tag
                      beside the first spent a column repeating it. The glyph is the catalog's, so a seat
                      is marked the same way as the card it was created from. */}
                    <SeatTypeChip seat={row.seat} />
                    {/* A seat can outlive its adapter's activation — created before the config gated
                      creation, or switched off afterwards — and then it bids on nothing however
                      completely it is mapped. The row has to say so, or it reads as an active seat. */}
                    {row.isGloballyDisabled && (
                      <Tooltip className={'flex!'} title="Adapter disabled in the Spark settings — no stack carries this seat">
                        <Icon width="20px" name="warning" fill={Colors.Warning} />
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              )}
              <TableCell>{bidAdjustment ?? <span className={'text-slate-grey'}>—</span>}</TableCell>
              <TableCell>
                {/* A scope-level seat has no media to count against, so it only ever answers mapped or not. */}
                {!row.isMapped ? (
                  <span className={'text-slate-grey'}>Not mapped</span>
                ) : isScopeLevelSeat(row.seat) ? (
                  'Mapped'
                ) : (
                  `Mapped on ${row.mappedSitesCount}/${mediaCountOfSeat(row.seat, mediaCounts)} media${row.mappedSitesCount > 1 ? 's' : ''}`
                )}
              </TableCell>
            </TableRow>
          )
        })
      ) : (
        <TableRow>
          <NoResultsTableCell colspan={columnsOfTab(tab).length}>No results</NoResultsTableCell>
        </TableRow>
      )}
    </Table>
  )

  /**
   * What a tab renders: its table, or the catalog invitation when the tab has never held a seat.
   *
   * Asked of `tabsWithSeats`, which is built from the *unfiltered* rows, and that is the whole point — a
   * search matching nothing must still read "No results". Answering "you have no seat" there would be a
   * lie the user can act on, sending them off to create a seat they already have. Loading is excluded for
   * the same reason the page-level state excludes it: every tab would otherwise flash the invitation
   * before its seats arrive.
   */
  const seatsPanel = (tab: SeatTab, tabRows: SeatRow[]) => {
    const invitation = emptySeatTabInvitation(tab)
    return invitation && !isLoading && !tabsWithSeats.includes(tab) ? noSeatsMessage(invitation) : seatsTable(tab, tabRows)
  }

  /**
   * Which of the seats below a request can never reach. See `unreachableSeatsWarning`: both kinds of
   * internal seat are called through a seat the customer owns, and an internal seat left without its
   * counterpart keeps being provisioned, mapped and carried in a stack while nothing calls it.
   *
   * Asked of every seat the page holds rather than of the tab's own rows: the counterpart it looks for is
   * by definition on another tab. Held back while the page loads for the same reason the empty-tab
   * invitation is — the publisher's seat arrives in a different request from the internal ones, so a tab
   * rendered mid-flight would accuse a scope of missing a seat it has.
   */
  const unreachableSeatsAlert = (tab: SeatTab) => {
    const warning = isLoading ? undefined : unreachableSeatsWarning(tab, bidderSeats)
    return (
      warning && (
        <Alert variant={'warning'} title={warning.title} iconName={'warning'}>
          {warning.detail}
        </Alert>
      )
    )
  }

  /**
   * Spark's own scope-level settings, sharing the tab's action row rather than taking one of their own:
   * a line holding two chips and nothing else pushes the table down for a status nobody came to read.
   *
   * They describe how Spark runs on this scope rather than any one seat, which is why they sit inside the
   * Spark tabs and not above the bar: at page level they read as a property of "my bidder seats", which
   * they are not, and every customer-facing tab would have to carry them past to reach its table.
   * Staff-only, and the `isPubstackUser` guard here is what makes that true — the Spark for Pubs tab
   * below is a tab the customer opens too.
   */
  /**
   * The stack each Spark tab describes. Scope-level, so it is asked for by type — and staff-only like the
   * tabs themselves, which is why it is not fetched at all for anyone else.
   */
  const { stack: sparkDemandStack } = useScopeLevelStack('pbstck-bidder', isPubstackUser && showSparkDemandTab)
  const { stack: sparkForPubStack } = useScopeLevelStack('spark-for-pub', isPubstackUser && showSparkForPubTab)

  /**
   * The generated configuration behind the tab, opened as JSON rather than rendered.
   *
   * The same affordance a media's server-side stack already gives (`StackTableRow`): these outputs are
   * read to answer "what is Prebid Server actually being told", and a page that paraphrased them would be
   * one more thing to keep true. Absent until a stack exists, which is the honest state before onboarding.
   */
  const stackConfigButton = (stack: Stack | undefined, trackingLabel: TrackingLabel) =>
    stack && (
      <Button variant={'secondary'} iconName={'open_in_new'} onClick={() => openJsonInNewTab(stack.output ?? {})} trackingLabel={trackingLabel}>
        {'View stack configuration'}
      </Button>
    )

  /**
   * What each tab shows under the bar. The two Spark ones used to carry a standing notice explaining that
   * the seats under them are Pubstack's; only staff ever reaches them (see `visibleSeatTabs`), so the
   * notice told its audience what it already knew. The shield on the tab is what marks them now.
   */
  const panelOfTab: Record<SeatTab, ReactNode> = {
    'client-side': seatsPanel('client-side', clientSideRows),
    'server-side': seatsPanel('server-side', serverSideRows),
    'spark-for-pub': (
      <>
        {unreachableSeatsAlert('spark-for-pub')}
        {/* No bulk action on this tab, so nothing to share a row with — the stack config button takes it
            alone. Kept out entirely when there is no stack yet: the page column gaps its children, so an empty
            row still pushes the table down, and this is a tab the customer opens. */}
        {sparkForPubStack && <div className={'flex items-center justify-end gap-2'}>{stackConfigButton(sparkForPubStack, 'common-my-bidder-seats-spark-for-pub-stack-config-apply')}</div>}
        {seatsPanel('spark-for-pub', sparkForPubRows)}
      </>
    ),
    'spark-demand': (
      <>
        {unreachableSeatsAlert('spark-demand')}
        <div className={'flex items-center justify-end gap-2'}>
          {/* Adding one seat moved up to the header, where the page's add action lives; creating the whole
              set is particular to this tab, so it stays with the table it fills. */}
          <Button
            variant="secondary"
            disabled={isBulkCreating || isCreateAllCandidateSetUnknown || missingSparkDemandBidders.length === 0}
            iconName={isBulkCreating ? 'pubstack' : undefined}
            iconSpin={isBulkCreating}
            onClick={handleCreateAllSparkDemandSeatsClick}
            trackingLabel={CREATE_ALL_SPARK_DEMAND_SEATS_TRACKING_LABEL}
          >
            {createAllSeatsLabel}
          </Button>
          {stackConfigButton(sparkDemandStack, 'common-my-bidder-seats-spark-demand-stack-config-apply')}
        </div>
        {seatsPanel('spark-demand', sparkDemandRows)}
      </>
    ),
  }

  /** Given its own row under the tabs, so it takes the width the rule lists give their search. */
  const filterBar = <FilterBar onSearchChange={(e) => setSearch(e)} resetFiltersTrackingLabel={RESET_FILTERS_TRACKING_LABEL} searchClassName={SEARCH_CLASSES} />

  // Spelled out rather than mapped over `visibleSeatTabs`: React Compiler cannot preserve this component's
  // memoization once it has to reason about a closure over that array, and bails on the whole component.
  const tabProp = (tab: SeatTab): TabProp => ({
    label: SEAT_TAB_LABEL[tab],
    trackingLabel: SEAT_TAB_TRACKING_LABEL[tab],
    active: tab === activeTab,
    // `TabProp.label` is a plain string, so the marker has to be the icon. The shield is what says
    // "Pubstack only" across the app; red is not — it read as a warning about the seats rather than as a
    // note about who owns them, so the tab keeps the ordinary colour and lets the shield carry the meaning.
    iconName: isInternalSeatTab(tab) ? 'admin' : undefined,
  })
  const tabProps: TabProp[] = [
    ...(showClientSideTab ? [tabProp('client-side')] : []),
    ...(showServerSideTab ? [tabProp('server-side')] : []),
    ...(showSparkForPubTab ? [tabProp('spark-for-pub')] : []),
    ...(showSparkDemandTab ? [tabProp('spark-demand')] : []),
  ]

  // `Tabs` hands back the clicked tab, not its id — the label is what identifies it.
  const handleTabClick = (clicked: TabProp) => {
    const tab = seatTabOfLabel(clicked.label)
    // Re-clicking the open tab would otherwise push a history entry that goes nowhere, so Back would
    // appear to do nothing.
    if (tab && tab !== activeTab) {
      void setSelectedTab(tab)
    }
  }

  // No upsell branch here: the route itself is gated on owning One or Node, so a scope on neither
  // never reaches this page and never sees the entry in the menu. The Catalog carries the pitch —
  // it stays browsable for them, every bidder greyed out.
  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <div className={'flex gap-2'}>
          {/* One add action, and which one depends on whose tab is open. Spark Demand's seats are ours to
              create on the customer's behalf, so on that tab the header offers that and not "New seat",
              which would make a `pbs` seat nobody asked for. The tab is staff-only and `activeTab` only
              ever resolves to a visible tab, but the gate is repeated here rather than inferred. */}
          {showSparkDemandTab && activeTab === 'spark-demand' ? (
            <Button variant="tertiary" iconName="add_circle" onClick={handleAddSparkDemandSeatClick} trackingLabel={ADD_SPARK_DEMAND_SEAT_TRACKING_LABEL}>
              Add spark demand seat
            </Button>
          ) : (
            /* A seat is what a bidder is sent for every ad unit on the scope, so making one is an owner's.
               The button stays on screen saying why — seeing that the capability exists is half of
               knowing to ask an owner for it. */
            <Tooltip title={disabledReason} disabled={canCreate}>
              <Button variant="tertiary" iconName="add_circle" disabled={!canCreate} onClick={handleNewSeatClick} trackingLabel={NEW_SEAT_TRACKING_LABEL}>
                New seat
              </Button>
            </Tooltip>
          )}
          {backToSite ? (
            // The Node activation page is the only thing that ever sets `backToSite`, so the media it
            // names is a Node one. This page no longer has a product of its own to infer it from.
            <Button trackingLabel={BACK_TO_SITE_TRACKING_LABEL} onClick={() => navigate(`${PATH_BY_PRODUCT.pbs}/sites/${backToSite}`)} variant="secondary">
              Back to site
            </Button>
          ) : undefined}
        </div>
      }
    >
      <div className={'flex flex-col gap-4'}>
        {/* No integration at all, on any tab. Kept as its own state rather than left to the per-tab one
            below: with nothing anywhere, a tab bar is a choice between two empty screens, and the thing
            to say is the shorter one. The search goes with them — there is nothing to search. */}
        {!isLoading && bidderSeats.length === 0 ? (
          noSeatsMessage('You don’t have any integration yet.')
        ) : tabProps.length <= 1 ? (
          // A lone tab is just a heading: a scope with one integration gets the table it always had.
          <>
            {filterBar}
            {panelOfTab[activeTab]}
          </>
        ) : (
          <>
            <Tabs tabs={tabProps} onClick={handleTabClick} />
            {/* Under the tabs rather than above them: the search filters the open tab's table, and sitting
                above the bar it read as filtering the tabs themselves. */}
            {filterBar}
            {panelOfTab[activeTab]}
          </>
        )}
      </div>
    </PageContent>
  )
}
